import { useState, useEffect, useId, useRef } from "react";
import { Dialog } from "@headlessui/react";
import Select from "react-select";
import { useTheme } from '../../CommonComponents/ThemeProvider';
import tinycolor from "tinycolor2";
import { AlertTriangle, Barcode, Box, Camera, CheckCircle2, ImagePlus, Loader2, Trash2, Wand2, X } from "lucide-react";
import { fetchBranches } from "../../../services/branchApi";
import { PRODUCT_CATEGORY_OPTIONS } from "../../../constants/productCategories";
import ReactSelectField from "../../CommonComponents/ReactSelectField";
import axiosInstance from "../../../services/axiosInstance";
import { showErrorToast } from "../../../utils/toast";
import { resolveApiUrl } from "../../../utils/apiBaseUrl";
import { getBarcodeValidation, normalizeBarcode } from "../../../utils/barcodeUtils";

const unitTypeOptions = [
    { value: "pcs", label: "Piece" },
    { value: "pack", label: "Pack" },
    { value: "box", label: "Box" },
    { value: "bottle", label: "Bottle" },
    { value: "can", label: "Can" },
    { value: "sachet", label: "Sachet" },
    { value: "bag", label: "Bag" },
    { value: "kg", label: "Kg" },
    { value: "gram", label: "Gram" },
    { value: "liter", label: "Liter" },
    { value: "ml", label: "Milliliter" },
    { value: "dozen", label: "Dozen" },
    { value: "roll", label: "Roll" },
    { value: "bundle", label: "Bundle" },
    { value: "set", label: "Set" },
];

const barcodeTypeOptions = [
    { value: "MANUFACTURER", label: "Manufacturer / GS1" },
    { value: "INTERNAL", label: "Internal Barcode" },
];

const createSelectStyles = (themeColor) => {
    const hoverTint = tinycolor(themeColor).setAlpha(0.08).toRgbString();

    return {
        control: (base, state) => ({
            ...base,
            minHeight: 38,
            height: 38,
            borderRadius: 16,
            borderColor: state.isFocused ? themeColor : "#e2e8f0",
            backgroundColor: "#fff",
            boxShadow: state.isFocused
                ? `0 0 0 1px ${themeColor}, 0 8px 20px rgba(15,23,42,0.04)`
                : "0 8px 20px rgba(15,23,42,0.04)",
            "&:hover": { borderColor: themeColor },
            fontSize: 14,
        }),
        valueContainer: (base) => ({
            ...base,
            minHeight: 36,
            height: 36,
            padding: "0 12px",
        }),
        input: (base) => ({
            ...base,
            margin: 0,
            padding: 0,
        }),
        indicatorsContainer: (base) => ({
            ...base,
            minHeight: 36,
            height: 36,
        }),
        menu: (base) => ({
            ...base,
            borderRadius: 16,
            border: "1px solid #e2e8f0",
            overflow: "hidden",
            zIndex: 9999,
            boxShadow: "0 18px 45px rgba(15,23,42,0.16)",
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected
                ? themeColor
                : state.isFocused
                    ? hoverTint
                    : "#fff",
            color: state.isSelected ? "#fff" : "#0f172a",
            fontSize: 14,
        }),
        singleValue: (base) => ({
            ...base,
            color: "#0f172a",
        }),
        placeholder: (base) => ({
            ...base,
            color: "#94a3b8",
        }),
        indicatorSeparator: () => ({ display: "none" }),
        dropdownIndicator: (base, state) => ({
            ...base,
            padding: 4,
            color: state.isFocused ? themeColor : "#64748b",
            "&:hover": { color: themeColor },
        }),
    };
};

export default function AddProductModal({ isOpen, onClose, onSubmit, editData }) {
    const { currentTheme } = useTheme();
    const primaryColor = currentTheme.color;
    const hoverColor = tinycolor(primaryColor).darken(10).toString();
    const selectStyles = createSelectStyles(primaryColor);
    const uploadsBaseURL = resolveApiUrl("/uploads");
    const uploadInputId = useId();
    const mountedRef = useRef(false);

    const [productData, setProductData] = useState({
        name: "",
        category: "Others",
        barcode: "",
        barcode_type: "MANUFACTURER",
        price: "",
        unit_type: "pcs",
        description: "",
        branch_id: "",
        branch_price: "",
        image: null,
    });

    const [branches, setBranches] = useState([]);
    const [preview, setPreview] = useState(null);
    const [removeImage, setRemoveImage] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [generatingBarcode, setGeneratingBarcode] = useState(false);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        let isMounted = true;
        const loadBranches = async () => {
            try {
                const response = await fetchBranches();
                const branchList = response.data?.data || response.data || [];
                if (isMounted) {
                    setBranches(Array.isArray(branchList)
                        ? branchList.filter((branch) => String(branch.status || "").toUpperCase() === "ACTIVE")
                        : []);
                }
            } catch (error) {
                if (isMounted) setBranches([]);
            }
        };
        if (isOpen) loadBranches();
        return () => {
            isMounted = false;
        };
    }, [isOpen]);

    useEffect(() => {
        if (editData) {
            const existingImage =
                editData.image
                || (editData.image_url
                    ? (String(editData.image_url).startsWith("http")
                        ? editData.image_url
                        : `${uploadsBaseURL}/${editData.image_url}`)
                    : null);

            setProductData({
                name: editData.name || "",
                category: editData.category || "Others",
                barcode: editData.product_code || editData.barcode || "",
                barcode_type: editData.barcode_type || (getBarcodeValidation(editData.product_code || editData.barcode || "").isValidGs1Format ? "MANUFACTURER" : "INTERNAL"),
                price: editData.price || "",
                unit_type: editData.unit_type || "",
                description: editData.description || "",
                branch_id: "",
                branch_price: "",
                image: null,
            });
            setPreview(existingImage);
            setRemoveImage(false);
        } else {
            setProductData({ name: "", category: "Others", barcode: "", barcode_type: "MANUFACTURER", price: "", unit_type: "pcs", description: "", branch_id: "", branch_price: "", image: null });
            setPreview(null);
            setRemoveImage(false);
        }
        setSubmitting(false);
    }, [editData, uploadsBaseURL]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "branch_id") {
            const selectedBranchPrice = (editData?.branchPrices || []).find(
                (branchPrice) => String(branchPrice.branch_id) === String(value)
            );
            setProductData({
                ...productData,
                branch_id: value,
                branch_price: value ? (selectedBranchPrice?.price || "") : "",
            });
            return;
        }
        setProductData({ ...productData, [name]: value });
    };

    const branchOptions = [
        { value: "", label: "All branches (universal price only)" },
        ...branches.map((branch) => ({
            value: String(branch.id),
            label: branch.name || branch.location || `Branch #${branch.id}`,
        })),
    ];

    const handleUnitTypeChange = (option) => {
        setProductData((prev) => ({ ...prev, unit_type: option?.value || "" }));
    };

    const handleCategoryChange = (option) => {
        setProductData((prev) => ({ ...prev, category: option?.value || "Others" }));
    };

    const handleBarcodeTypeChange = (option) => {
        setProductData((prev) => ({ ...prev, barcode_type: option?.value || "MANUFACTURER" }));
    };

    const handleGenerateInternalBarcode = async () => {
        setGeneratingBarcode(true);
        try {
            const response = await axiosInstance.get("/admin/products/barcode/internal", {
                params: {
                    name: productData.name || "Item",
                    category: productData.category || "Others",
                },
            });
            setProductData((prev) => ({
                ...prev,
                barcode_type: "INTERNAL",
                barcode: response.data?.barcode || prev.barcode,
            }));
        } catch (error) {
            console.error("Failed to generate internal barcode:", error);
            showErrorToast(error.response?.data?.message || "Failed to generate internal barcode.");
        } finally {
            setGeneratingBarcode(false);
        }
    };

    const handleBranchScopeChange = (option) => {
        const value = option?.value || "";
        const selectedBranchPrice = (editData?.branchPrices || []).find(
            (branchPrice) => String(branchPrice.branch_id) === String(value)
        );
        setProductData((prev) => ({
            ...prev,
            branch_id: value,
            branch_price: value ? (selectedBranchPrice?.price || "") : "",
        }));
    };

    const handleImageChange = (file) => {
        setProductData({ ...productData, image: file });
        setPreview(file ? URL.createObjectURL(file) : null);
        setRemoveImage(false);
    };

    const handleImageRemove = () => {
        setProductData({ ...productData, image: null });
        setPreview(null);
        setRemoveImage(Boolean(editData?.image_url || editData?.image));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;

        const formData = new FormData();
        const normalizedBarcode = normalizeBarcode(productData.barcode);
        const barcodeValidation = getBarcodeValidation(normalizedBarcode);
        if (!normalizedBarcode) {
            showErrorToast("Barcode is required.");
            return;
        }
        if (productData.barcode_type === "MANUFACTURER" && !barcodeValidation.isValidGs1Format) {
            showErrorToast("Manufacturer barcode must be valid UPC/EAN/GTIN format. Use Internal Barcode for non-GS1 items.");
            return;
        }

        formData.append("name", productData.name);
        formData.append("category", productData.category || "Others");
        formData.append("barcode", normalizedBarcode);
        formData.append("product_code", normalizedBarcode);
        formData.append("barcode_type", productData.barcode_type || "MANUFACTURER");
        formData.append("price", productData.price);
        formData.append("unit_type", productData.unit_type);
        formData.append("description", productData.description);
        formData.append("branch_id", productData.branch_id || "");
        formData.append("branch_price", productData.branch_price || "");
        formData.append("removeImage", removeImage ? "true" : "false");

        if (productData.image) {
            formData.append("image", productData.image);
        }

        setSubmitting(true);
        try {
            await onSubmit(formData);
        } finally {
            if (mountedRef.current) setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    const barcodeValidation = getBarcodeValidation(productData.barcode);
    const isManufacturerBarcode = productData.barcode_type === "MANUFACTURER";
    const barcodeStatus = !productData.barcode
        ? null
        : isManufacturerBarcode && barcodeValidation.isValidGs1Format
            ? { tone: "success", text: "Valid UPC/EAN/GTIN format." }
            : isManufacturerBarcode
                ? { tone: "warning", text: "This does not match UPC/EAN/GTIN format. Use Internal Barcode for non-GS1 items." }
                : { tone: "success", text: "Internal barcode will print as Code 128." };

    return (
        <Dialog
            open={isOpen}
            onClose={() => {
                if (!submitting) onClose();
            }}
            className="fixed inset-0 z-50 overflow-hidden bg-slate-950/45 px-4 py-4 backdrop-blur-sm"
        >
            <div className="flex min-h-full w-full items-center justify-center">
                <Dialog.Panel className="relative my-auto flex max-h-[calc(100vh-2rem)] w-full max-w-lg flex-col overflow-hidden rounded-[28px] border border-white/70 bg-white shadow-[0_26px_70px_rgba(15,23,42,0.16)]">
                    <div className="absolute inset-x-0 top-0 h-28 bg-[linear-gradient(135deg,rgba(15,23,42,0.05),rgba(148,163,184,0.04),transparent)]" />
                    <button
                        type="button"
                        onClick={() => {
                            if (!submitting) onClose();
                        }}
                        disabled={submitting}
                        className="absolute right-4 top-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white/95 text-slate-500 shadow-sm transition hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
                        aria-label="Close product modal"
                    >
                        <X className="h-5 w-5" />
                    </button>

                    <div className="relative flex min-h-0 flex-col">
                        <div className="shrink-0 px-3.5 pt-3.5">
                            <div className="flex items-start gap-2.5 pb-3">
                                <div
                                    className="inline-flex h-11 w-11 items-center justify-center rounded-2xl text-white shadow-sm"
                                    style={{ background: `linear-gradient(135deg, ${primaryColor}, ${hoverColor})` }}
                                >
                                    <Box className="h-4.5 w-4.5" />
                                </div>
                                <div className="min-w-0">
                                    <Dialog.Title className="text-lg font-semibold tracking-tight text-slate-900">
                                        {editData ? "Edit Product" : "Add Product"}
                                    </Dialog.Title>
                                    <p className="mt-0.5 max-w-md text-[13px] leading-4.5 text-slate-500">
                                        {editData
                                            ? "Update the product details and refresh its image without leaving this workspace."
                                            : "Create a polished product listing with complete details and an optional image."}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="min-h-0 overflow-y-auto overflow-x-hidden px-3.5 pb-3.5">
                            <form onSubmit={handleSubmit} className="grid gap-3">
                                <fieldset disabled={submitting} className="contents disabled:cursor-wait">
                                <div className="grid gap-2.5 rounded-[22px] border border-slate-200 bg-slate-50/80 p-3 sm:grid-cols-2">
                                    <label className="block sm:col-span-2">
                                        <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                                            Product Name
                                        </span>
                                        <input
                                            name="name"
                                            placeholder="Enter product name"
                                            value={productData.name}
                                            onChange={handleChange}
                                            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 shadow-sm transition focus:outline-none"
                                            style={{ boxShadow: "0 8px 20px rgba(15,23,42,0.04)" }}
                                            required
                                        />
                                    </label>

                                    <label className="block">
                                        <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                                            Category
                                        </span>
                                        <ReactSelectField
                                            options={PRODUCT_CATEGORY_OPTIONS}
                                            value={PRODUCT_CATEGORY_OPTIONS.find((option) => option.value === productData.category) || PRODUCT_CATEGORY_OPTIONS[PRODUCT_CATEGORY_OPTIONS.length - 1]}
                                            onChange={handleCategoryChange}
                                            placeholder="Select category"
                                            isSearchable
                                            isDisabled={submitting}
                                            ariaLabel="Select product category"
                                        />
                                        <input type="hidden" name="category" value={productData.category} required />
                                    </label>

                                    <label className="block">
                                        <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                                            Barcode Type
                                        </span>
                                        <ReactSelectField
                                            options={barcodeTypeOptions}
                                            value={barcodeTypeOptions.find((option) => option.value === productData.barcode_type) || barcodeTypeOptions[0]}
                                            onChange={handleBarcodeTypeChange}
                                            placeholder="Select barcode type"
                                            isSearchable={false}
                                            isDisabled={submitting}
                                            ariaLabel="Select barcode type"
                                        />
                                    </label>

                                    <label className="block sm:col-span-2">
                                        <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                                            Barcode
                                        </span>
                                        <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr),auto]">
                                            <div className="relative">
                                                <Barcode className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                                <input
                                                    name="barcode"
                                                    placeholder={isManufacturerBarcode ? "Scan or enter UPC/EAN/GTIN" : "Generate or enter internal barcode"}
                                                    value={productData.barcode}
                                                    onChange={(event) => setProductData((prev) => ({ ...prev, barcode: normalizeBarcode(event.target.value) }))}
                                                    className="w-full rounded-2xl border border-slate-200 bg-white py-2 pl-10 pr-4 text-sm text-slate-900 shadow-sm transition focus:outline-none"
                                                    style={{ boxShadow: "0 8px 20px rgba(15,23,42,0.04)" }}
                                                    required
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={handleGenerateInternalBarcode}
                                                disabled={submitting || generatingBarcode}
                                                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-wait disabled:opacity-60"
                                            >
                                                {generatingBarcode ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                                                Generate Internal
                                            </button>
                                        </div>
                                        {barcodeStatus && (
                                            <div className={`mt-2 flex items-start gap-2 rounded-2xl border px-3 py-2 text-xs ${
                                                barcodeStatus.tone === "success"
                                                    ? "border-emerald-100 bg-emerald-50 text-emerald-700"
                                                    : "border-amber-100 bg-amber-50 text-amber-700"
                                            }`}>
                                                {barcodeStatus.tone === "success" ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" /> : <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
                                                <span>{barcodeStatus.text}</span>
                                            </div>
                                        )}
                                    </label>

                                    <label className="block sm:col-span-2">
                                        <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                                            Universal Price
                                        </span>
                                        <input
                                            name="price"
                                            type="number"
                                            placeholder="0.00"
                                            value={productData.price}
                                            onChange={handleChange}
                                            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 shadow-sm transition focus:outline-none"
                                            style={{ boxShadow: "0 8px 20px rgba(15,23,42,0.04)" }}
                                            min="0"
                                            step="0.01"
                                            required
                                        />
                                    </label>

                                    <label className="block sm:col-span-2">
                                        <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                                            Unit Type
                                        </span>
                                        <Select
                                            classNamePrefix="react-select"
                                            styles={selectStyles}
                                            options={unitTypeOptions}
                                            value={unitTypeOptions.find((option) => option.value === productData.unit_type) || null}
                                            onChange={handleUnitTypeChange}
                                            placeholder="Select unit type"
                                            isSearchable={false}
                                            isDisabled={submitting}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                        />
                                        <input type="hidden" name="unit_type" value={productData.unit_type} required />
                                    </label>

                                    <label className="block sm:col-span-2">
                                        <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                                            Branch Price Scope
                                        </span>
                                        <Select
                                            classNamePrefix="react-select"
                                            styles={selectStyles}
                                            options={branchOptions}
                                            value={branchOptions.find((option) => option.value === String(productData.branch_id)) || branchOptions[0]}
                                            onChange={handleBranchScopeChange}
                                            placeholder="Select branch scope"
                                            isSearchable
                                            isDisabled={submitting}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                        />
                                    </label>

                                    {productData.branch_id && (
                                        <label className="block sm:col-span-2">
                                            <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                                                Branch Price Optional
                                            </span>
                                            <input
                                                name="branch_price"
                                                type="number"
                                                placeholder="Leave blank or 0 to use universal price"
                                                value={productData.branch_price}
                                                onChange={handleChange}
                                                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 shadow-sm transition focus:outline-none"
                                                style={{ boxShadow: "0 8px 20px rgba(15,23,42,0.04)" }}
                                                min="0"
                                                step="0.01"
                                            />
                                        </label>
                                    )}

                                    <label className="block sm:col-span-2">
                                        <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                                            Description
                                        </span>
                                        <textarea
                                            name="description"
                                            placeholder="Add a short product description"
                                            value={productData.description}
                                            onChange={handleChange}
                                            className="min-h-[60px] w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 shadow-sm transition focus:outline-none"
                                            style={{ boxShadow: "0 8px 20px rgba(15,23,42,0.04)" }}
                                            rows={1}
                                        />
                                    </label>
                                </div>

                                <div className="rounded-[22px] border border-slate-200 bg-white p-3 shadow-sm">
                                    <div className="mb-2.5 flex items-center gap-2.5">
                                        <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-100 text-slate-500">
                                            <ImagePlus className="h-4.5 w-4.5" />
                                        </div>
                                        <div>
                                            <h3 className="text-sm font-semibold text-slate-900">Product Image</h3>
                                            <p className="text-xs leading-4 text-slate-500">
                                                Upload a square or portrait image for a cleaner product card preview.
                                            </p>
                                        </div>
                                    </div>

                                    <input
                                        id={uploadInputId}
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) handleImageChange(file);
                                        }}
                                    />

                                    <div className="min-w-0 flex flex-col gap-3 sm:flex-row sm:items-center">
                                        <div className="relative mx-auto sm:mx-0">
                                            <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_10px_24px_rgba(15,23,42,0.08)]">
                                                {preview ? (
                                                    <img
                                                        src={preview}
                                                        alt="Product preview"
                                                        className="h-full w-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="flex h-full w-full items-center justify-center bg-slate-100 text-slate-400">
                                                        <ImagePlus className="h-9 w-9" />
                                                    </div>
                                                )}
                                            </div>
                                            {preview ? (
                                                <button
                                                    type="button"
                                                    onClick={handleImageRemove}
                                                    className="absolute -right-2 -top-2 inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/80 bg-white text-slate-500 shadow-lg shadow-slate-200/80 transition hover:bg-rose-50 hover:text-rose-600 focus:outline-none"
                                                    aria-label="Delete product image"
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                            ) : null}
                                        </div>

                                        <div className="min-w-0 flex-1 overflow-hidden">
                                            <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
                                                <label
                                                    htmlFor={uploadInputId}
                                                    className={`inline-flex shrink-0 items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow-[0_10px_20px_rgba(15,23,42,0.14)] transition hover:bg-slate-800 ${submitting ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                                                >
                                                    <Camera className="h-4 w-4" />
                                                    Choose Image
                                                </label>
                                                <div className="min-w-0 w-full flex-1 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500 sm:w-auto sm:max-w-[220px]">
                                                    <span className="block w-full overflow-hidden text-ellipsis whitespace-nowrap">
                                                        {productData.image?.name || "No file selected"}
                                                    </span>
                                                </div>
                                            </div>
                                            <p className="mt-2 text-xs leading-4 text-slate-500">
                                                PNG or JPG works best. Leave this unchanged to keep the current image.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-end gap-2 pt-0.5">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        disabled={submitting}
                                        className="rounded-2xl border border-slate-200 bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-200 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="inline-flex min-w-[132px] items-center justify-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold text-white shadow-[0_14px_30px_rgba(15,23,42,0.16)] transition hover:opacity-90 focus:outline-none disabled:cursor-wait disabled:opacity-75"
                                        style={{ background: `linear-gradient(135deg, ${primaryColor}, ${hoverColor})` }}
                                    >
                                        {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                                        {submitting
                                            ? (editData ? "Updating..." : "Adding...")
                                            : (editData ? "Update Product" : "Add Product")}
                                    </button>
                                </div>
                                </fieldset>
                            </form>
                        </div>
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
}
